import React, { useEffect, useState } from "react";
import { Grid, Typography } from "@mui/material";
import { ContractDetailTextField } from "@features/projects-feature/components/ContractDetailTextField";
import { ContractDetailFields } from "@features/projects-feature/utils/fields/ContractDetailsFields";
import { ServiceContractDetailsContainerProps } from "@features/projects-feature/projectTypes";
import {
  StyledServiceContractDetailsContainer,
  StyledServiceContractDetailsSubheader,
} from "@features/projects-feature/utils/components/StyledServiceContractDetailsComponents";
import { ItemBox } from "shared-ui";
import { formatDollarAmount } from "shared-ui/src/features/roadmaps-feature/utils/roadmapHelpers";
import { getContractRetainerSpendByContractId } from "@features/projects-feature/hooks/useGetContractRetainersByContractId";
import { useClaims } from "../../../../../auth/ClaimsProvider";
import { useFeatureFlag } from "shared-ui/src/utils/FeatureFlagHelper";
import { BillingTypes } from "@features/services-feature/contracts/constants";

const getSortedColumnFields = (columnKey: number) => {
  return [...ContractDetailFields]
    .filter((field) => field.columnKey === columnKey)
    .sort((a, b) => a.sortPriority - b.sortPriority);
};
export const ServiceContractDetailsContainer = ({
  contract,
  picklist,
}: ServiceContractDetailsContainerProps) => {
  const [contractRetainerSpend, setContractRetainerSpend] = useState({
    usage: {
      Consumed: 0,
      Pending: 0,
      "Total Usage": 0,
    },
    retainer: {
      "Retainer Amount": 0,
      "Current Balance": 0,
      "Remaining Balance": 0,
      "Total Invoiced": 0,
    },
  });

  const { getClaimValue } = useClaims();
  const contractDetailsColumnOne = getSortedColumnFields(1);
  const contractDetailsColumnTwo = getSortedColumnFields(2);

  const isImpersonating: boolean = !!getClaimValue("isImpersonating");
  const { enabled: contractRetainerSpendEnabled } = useFeatureFlag(
    "nomads.enable-contracts-retainer-spend",
  );
  const displayContractRetainerSpend: boolean =
    contract.billingTypeLabel === BillingTypes.retainer &&
    (contractRetainerSpendEnabled || isImpersonating);

  const { data, isSuccess } = getContractRetainerSpendByContractId(contract.id);

  useEffect(() => {
    if (isSuccess && data) {
      const {
        billedSpend = 0,
        pendingSpend = 0,
        billedOverage = 0,
        totalActiveRetainerAmount = 0,
      } = data;

      const consumed = billedSpend;
      const pending = pendingSpend;
      const totalUsage = consumed + pending;
      const retainerAmount = totalActiveRetainerAmount;
      const currentBalance = retainerAmount - consumed;
      const remainingBalance = retainerAmount - totalUsage;
      const totalInvoices = retainerAmount + billedOverage;

      setContractRetainerSpend({
        usage: {
          Consumed: consumed,
          Pending: pending,
          "Total Usage": totalUsage,
        },
        retainer: {
          "Retainer Amount": retainerAmount,
          "Current Balance": currentBalance,
          "Remaining Balance": remainingBalance,
          "Total Invoiced": totalInvoices,
        },
      });
    }
  }, [isSuccess, data]);

  return (
    <StyledServiceContractDetailsContainer container spacing={2}>
      <Grid item xs={12}>
        <StyledServiceContractDetailsSubheader>
          Service Contract
        </StyledServiceContractDetailsSubheader>
      </Grid>
      {contractDetailsColumnOne.length > 0 && (
        <Grid item xs={6}>
          {contractDetailsColumnOne.map((field) => {
            return (
              <ContractDetailTextField
                key={field.fieldKey}
                field={field}
                contract={contract}
                picklist={picklist}
              />
            );
          })}
        </Grid>
      )}
      {contractDetailsColumnTwo.length > 0 && (
        <Grid item xs={6}>
          {contractDetailsColumnTwo.map((field) => {
            return (
              <ContractDetailTextField
                key={field.fieldKey}
                field={field}
                contract={contract}
                picklist={picklist}
              />
            );
          })}
        </Grid>
      )}

      {displayContractRetainerSpend &&
        Object.entries(contractRetainerSpend).map(([category, details]) => (
          <Grid item xs={12} key={category}>
            <Typography variant="body1" gutterBottom padding={2}>
              {category.charAt(0).toUpperCase() + category.slice(1)}
            </Typography>
            <Grid container spacing={2}>
              {Object.entries(details).map(([key, value]) => (
                <Grid item xs={4} key={key}>
                  <ItemBox
                    label={key}
                    data={formatDollarAmount(value)}
                    labelVariant={"body1"}
                  />
                </Grid>
              ))}
            </Grid>
          </Grid>
        ))}
    </StyledServiceContractDetailsContainer>
  );
};
